(() => {
  if (window.qomoorPostHogStorefront) return;
  window.qomoorPostHogStorefront = true;

  const script = document.currentScript;
  const token = script?.dataset.qomoorPosthogToken || '';
  const host = script?.dataset.qomoorPosthogHost || 'https://us.i.posthog.com';
  const replaySampleRate = Number(script?.dataset.qomoorPosthogReplaySampleRate || '0.10');
  const visitorCookie = 'qm_ph_did';
  const visitorCartAttribute = '__qm_posthog_distinct_id';
  const contextCookie = 'qm_ph_context_v3';
  const qaAttribute = '__qm_posthog_qa';
  const sessionAttribute = '__qm_posthog_session_id';
  const sessionTimeAttribute = '__qm_posthog_session_time';
  const qaThemeId = '156793077916';
  const cookieMaxAge = 60 * 60 * 24 * 365;
  const blockedReplaySelector = [
    'form[action*="/account"]',
    'form[action*="/contact"]',
    'form[action*="/search"]',
    'form[action*="/cart"] textarea',
    'form[action*="/gift_cards"]',
    'input[type="email"]',
    'input[type="tel"]',
    'input[type="password"]',
    'input[type="search"]',
    'textarea',
    '[contenteditable="true"]',
    '[data-customer-account]',
    '[data-newsletter-form]',
    '.newsletter-form',
    '.predictive-search',
    'search-modal',
  ].join(',');
  let initialized = false;
  let identitySyncPromise;
  let contextSignature = '';
  let cartSignature = '';
  let cartRequested = false;

  const analyticsAllowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
    catch { return false; }
  };

  const readCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
  };

  const validVisitorId = (value) => /^qm_[0-9a-f-]{20,64}$/i.test(value || '');

  const createVisitorId = () => {
    const randomId = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `qm_${randomId}`;
  };

  const writeVisitorId = (visitorId) => {
    document.cookie = `${encodeURIComponent(visitorCookie)}=${encodeURIComponent(visitorId)}; Path=/; Max-Age=${cookieMaxAge}; SameSite=Lax; Secure`;
  };

  const getVisitorId = () => {
    const stored = readCookie(visitorCookie);
    if (validVisitorId(stored)) return stored;
    const visitorId = createVisitorId();
    writeVisitorId(visitorId);
    return visitorId;
  };

  const sampledForReplay = (visitorId) => {
    let hash = 2166136261;
    for (let index = 0; index < visitorId.length; index += 1) {
      hash ^= visitorId.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0) / 4294967296 < replaySampleRate;
  };

  const installPostHogStub = () => {
    if (window.posthog?.__SV) return window.posthog;
    const posthog = (window.posthog = window.posthog || []);
    const methods = [
      'capture',
      'identify',
      'opt_in_capturing',
      'opt_out_capturing',
      'startSessionRecording',
      'stopSessionRecording',
      'set_config',
    ];
    posthog._i = posthog._i || [];
    posthog.init = (projectToken, config, name) => {
      const instance = name ? (posthog[name] = posthog[name] || []) : posthog;
      for (const method of methods) {
        instance[method] = (...args) => instance.push([method, ...args]);
      }
      posthog._i.push([projectToken, config, name]);
      if (!document.querySelector('script[data-qomoor-posthog-sdk]')) {
        const sdk = document.createElement('script');
        sdk.async = true;
        sdk.src = `${host.replace(/\/$/, '')}/static/array.js`;
        sdk.dataset.qomoorPosthogSdk = 'true';
        document.head.appendChild(sdk);
      }
      return instance;
    };
    posthog.__SV = 1;
    return posthog;
  };

  const publishContext = () => {
    if (!analyticsAllowed() || typeof window.posthog?.get_session_id !== 'function') return undefined;
    const distinctId = window.posthog.get_distinct_id();
    const sessionId = window.posthog.get_session_id();
    if (!validVisitorId(distinctId) || !/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(sessionId)) return undefined;
    if (readCookie(visitorCookie) !== distinctId) writeVisitorId(distinctId);
    const context = { version: 3, theme_id: qaThemeId, distinct_id: distinctId, session_id: sessionId, updated_at: Date.now() };
    document.cookie = `${contextCookie}=${encodeURIComponent(JSON.stringify(context))}; Path=/; Max-Age=1800; SameSite=Lax; Secure`;
    const signature = `${distinctId}:${sessionId}`;
    if (contextSignature !== signature) {
      contextSignature = signature;
      window.Shopify?.analytics?.publish('qomoor:posthog_context', context);
    }
    return context;
  };

  const syncVisitorIdToCart = async (visitorId) => {
    if (!analyticsAllowed() || !validVisitorId(visitorId)) return;
    cartRequested = true;
    if (identitySyncPromise) return identitySyncPromise;
    const context = publishContext();
    if (!context) return;
    const signature = `${context.distinct_id}:${context.session_id}`;
    if (cartSignature === signature) { cartRequested = false; return; }
    cartRequested = false;
    const root = window.Shopify?.routes?.root || '/';
    identitySyncPromise = fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: {
        [visitorCartAttribute]: context.distinct_id,
        [sessionAttribute]: context.session_id,
        [sessionTimeAttribute]: String(context.updated_at),
        [qaAttribute]: qaThemeId,
      } }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('PostHog visitor identity sync failed');
        cartSignature = signature;
      })
      .catch(() => undefined)
      .finally(() => {
        identitySyncPromise = undefined;
        if (cartRequested && analyticsAllowed()) syncVisitorIdToCart(getVisitorId());
      });
    return identitySyncPromise;
  };

  const initialize = () => {
    if (initialized || !token || !analyticsAllowed()) return;
    const visitorId = getVisitorId();
    const recordReplay = sampledForReplay(visitorId);
    const posthog = installPostHogStub();
    posthog.init(token, {
      api_host: host,
      defaults: '2026-05-30',
      bootstrap: { distinctID: visitorId, isIdentifiedID: false },
      persistence: 'cookie',
      persistence_name: 'qomoor_posthog',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      capture_dead_clicks: false,
      capture_performance: false,
      enable_recording_console_log: false,
      disable_session_recording: !recordReplay,
      disable_surveys: true,
      disable_web_experiments: true,
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,
      person_profiles: 'identified_only',
      session_recording: {
        maskAllInputs: true,
        blockSelector: blockedReplaySelector,
        recordCrossOriginIframes: false,
        capturePerformance: false,
        recordCanvas: false,
      },
      loaded: (sdk) => {
        if (!analyticsAllowed()) { stopForConsentWithdrawal(); return; }
        publishContext();
        if (typeof sdk.onSessionId === 'function') sdk.onSessionId(() => {
          publishContext();
          if (analyticsAllowed()) syncVisitorIdToCart(getVisitorId());
        });
        if (cartRequested || /\/(?:products\/|cart$)/.test(location.pathname)) syncVisitorIdToCart(getVisitorId());
      },
    });
    if (recordReplay) posthog.startSessionRecording();
    initialized = true;
  };

  const sourceSurface = (anchor) => {
    if (anchor.closest('article')) return 'article';
    if (anchor.closest('predictive-search, search-modal')) return 'search';
    if (anchor.closest('product-card')) return 'product_card';
    if (anchor.closest('product-card-link, .resource-card')) return 'resource_card';
    return 'storefront_link';
  };

  const publishProductClick = async (event) => {
    if (!event.isTrusted || !analyticsAllowed() || !(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href*="/products/"]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const destination = new URL(anchor.href, location.href);
    if (destination.origin !== location.origin) return;
    const handleMatch = destination.pathname.match(/\/products\/([^/?#]+)/);
    if (!handleMatch) return;
    const productContainer = anchor.closest('[data-product-id]');
    const selectedVariant = anchor.closest('product-card')?.querySelector('input[type="radio"]:checked[data-variant-id]');
    let productId = productContainer?.dataset.productId || anchor.dataset.itemId || '';
    let variantId = destination.searchParams.get('variant') || selectedVariant?.dataset.variantId || '';
    const publish = () => window.Shopify?.analytics?.publish('qomoor:product_click', {
      product_handle: decodeURIComponent(handleMatch[1]),
      product_id: productId,
      variant_id: variantId,
      source_surface: sourceSurface(anchor),
      source_path: location.pathname,
      destination_path: destination.pathname,
    });
    if (productId && variantId) {
      publish();
      return;
    }
    const canDelayNavigation = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && anchor.target !== '_blank';
    if (!canDelayNavigation) {
      publish();
      return;
    }
    event.preventDefault();
    try {
      const response = await fetch(`${destination.pathname}.js`, { credentials: 'same-origin' });
      if (response.ok) {
        const product = await response.json();
        productId = productId || String(product.id || '');
        const defaultVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];
        variantId = variantId || String(defaultVariant?.id || '');
      }
      await publish();
    } finally {
      location.assign(anchor.href);
    }
  };

  const stopForConsentWithdrawal = () => {
    if (window.posthog?.stopSessionRecording) window.posthog.stopSessionRecording();
    if (window.posthog?.opt_out_capturing) window.posthog.opt_out_capturing({ clear_persistence: true });
    document.cookie = `${contextCookie}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
    contextSignature = '';
    cartSignature = '';
  };

  const handleConsentChange = () => {
    if (analyticsAllowed()) {
      if (initialized && window.posthog?.opt_in_capturing) window.posthog.opt_in_capturing();
      initialize();
      publishContext();
    } else {
      stopForConsentWithdrawal();
    }
  };

  document.addEventListener('click', publishProductClick, { capture: true });
  document.addEventListener('pointerdown', () => publishContext(), { capture: true, passive: true });
  document.addEventListener('keydown', () => publishContext(), { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) publishContext(); });
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.didError || !analyticsAllowed()) return;
    syncVisitorIdToCart(getVisitorId());
  });
  document.addEventListener('visitorConsentCollected', handleConsentChange);
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!analyticsAllowed() || !(event.target instanceof Element)) return;
      if (!event.target.closest('.accelerated-checkout-block, .additional-checkout-buttons, a[href*="/checkout"]')) return;
      syncVisitorIdToCart(getVisitorId());
    },
    { capture: true, passive: true }
  );

  if (window.location.pathname.endsWith('/cart') && analyticsAllowed()) syncVisitorIdToCart(getVisitorId());
  handleConsentChange();
  // Retry only a missing API; never infer permission or modify consent.
  let privacyRetries = 0;
  const retryPrivacyApi = () => {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
      handleConsentChange();
      return;
    }
    if (++privacyRetries < 60) setTimeout(retryPrivacyApi, 500);
  };
  if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') {
    setTimeout(retryPrivacyApi, 500);
  }
})();


